import { reportClientError } from '../api/clientErrors.js'

export function makeLiveWs({ token: token, isLoggedIn, sync }) {
  const wsRef = { value: null }
  const reconnectTimer = { value: null }
  const intentional = new WeakSet()
  let generation = 0
  let connecting = null

  async function terminate() {
    generation++
    connecting = null
    try {
      if (reconnectTimer.value) {
        clearTimeout(reconnectTimer.value)
        reconnectTimer.value = null
      }
      if (wsRef.value) {
        intentional.add(wsRef.value)
        wsRef.value.close()
        wsRef.value = null
      }
    } catch (e) {
      console.warn('terminate error:', e)
    }
  }

  async function live(checkFn) {
    if (wsRef.value) return
    if (connecting) return connecting

    const currentGeneration = generation
    const pending = (async () => {
      const ok = await checkFn()
      if (!ok || currentGeneration !== generation || wsRef.value) return

        const qs = token.value ? `?token=${encodeURIComponent(token.value)}` : ''
        const url = `wss://${location.host}/live${qs}`
        const startedAt = performance.now()
        let openedAt = 0
        const ws = new WebSocket(url)
        wsRef.value = ws

        ws.onopen = () => {
          if (wsRef.value !== ws || generation !== currentGeneration) return
          openedAt = performance.now()
          reportClientError('info', 'websocket_open', {
            connectMs: Math.round((openedAt - startedAt) * 10) / 10,
          })
        }

        ws.onmessage = (evt) => {
          if (wsRef.value !== ws || generation !== currentGeneration) return
          try {
            const payload = JSON.parse(evt.data)
            if (payload.type === 'connected') return
            sync(payload)
          } catch (e) {
            console.warn('WS bad message:', e)
          }
        }

        ws.onerror = () => {
          if (wsRef.value !== ws || generation !== currentGeneration) return
          reportClientError('warn', 'websocket_error', {
            readyState: ws.readyState,
            uptimeMs: Math.round((performance.now() - startedAt) * 10) / 10,
            opened: openedAt > 0,
          })
        }

        ws.onclose = (event) => {
          const current = wsRef.value === ws && generation === currentGeneration
          const stopped = intentional.has(ws)
          const uptimeMs = Math.round((performance.now() - startedAt) * 10) / 10
          reportClientError(stopped || event.wasClean ? 'info' : 'warn', 'websocket_close', {
            code: event.code,
            reason: event.reason || '',
            wasClean: event.wasClean,
            intentional: stopped,
            opened: openedAt > 0,
            uptimeMs,
            connectedMs: openedAt > 0 ? Math.round((performance.now() - openedAt) * 10) / 10 : 0,
            reconnect: current && !stopped && isLoggedIn.value,
          })

          if (current) wsRef.value = null
          if (current && !stopped && !reconnectTimer.value && isLoggedIn.value) {
            reconnectTimer.value = setTimeout(() => {
              reconnectTimer.value = null
              live(checkFn).catch(console.error)
            }, 2000)
          }
        }
    })()
    connecting = pending
    try {
      await pending
    } finally {
      if (connecting === pending) connecting = null
    }
  }

  return { live, terminate }
}
